import type { ReactNode } from 'react';
import { motion, useReducedMotion, type HTMLMotionProps } from 'motion/react';
import { buttonVariants, type ButtonVariants } from '@/components/ui/button-variants';
import { cn } from '@/lib/utils/cn';

type MotionButtonProps = HTMLMotionProps<'button'>;

interface ButtonProps extends Omit<MotionButtonProps, 'children'>, ButtonVariants {
  loading?: boolean;
  children: ReactNode;
}

/** Botão padrão Aura — micro-interação spring via Motion (respeita reduced-motion). */
export function Button({
  variant,
  size,
  block,
  loading,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const reduce = useReducedMotion();
  const classes = cn(buttonVariants({ variant, size, block }), className);

  const interaction =
    reduce || disabled || loading
      ? {}
      : {
          whileHover: { y: -1 },
          whileTap: { y: 0, scale: 0.985 },
          transition: { type: 'spring' as const, stiffness: 420, damping: 30 },
        };

  return (
    <motion.button
      type={type}
      disabled={disabled || loading}
      className={classes}
      aria-busy={loading || undefined}
      {...rest}
      {...interaction}
    >
      {loading ? <ButtonSpinner /> : children}
    </motion.button>
  );
}

function ButtonSpinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
